import React, { useCallback, useEffect, useState } from "react";
import { ShieldAlert, Check, X } from "lucide-react";
import { apiClient } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const fmtIban = (v) => (v ? v.replace(/(.{4})/g, "$1 ").trim() : "—");

export const IbanApprovalBanner = ({ companyId, compact = false, onChanged }) => {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    if (!companyId) return;
    try {
      const r = await apiClient.get(`/companies/${companyId}/employees/iban-approvals`);
      setItems(r.data?.items || []);
    } catch (e) {
      console.error(e);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const decide = async (employeeId, action) => {
    if (action === "reject" && !window.confirm("IBAN-Änderung ablehnen? Die bisherige IBAN wird wiederhergestellt.")) return;
    setBusy(employeeId + action);
    try {
      await apiClient.post(`/companies/${companyId}/employees/${employeeId}/iban-approval`, { action });
      toast.success(action === "approve" ? "IBAN freigegeben – Lohnzahlung wieder möglich" : "IBAN-Änderung abgelehnt – alte IBAN wiederhergestellt");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aktion fehlgeschlagen");
    } finally {
      setBusy(null);
    }
  };

  if (!items.length) return null;

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="iban-approval-banner">
      <div className="flex items-center gap-2 text-amber-900 font-semibold">
        <ShieldAlert className="h-5 w-5" />
        <span data-testid="iban-approval-count">{items.length} IBAN-Änderung{items.length > 1 ? "en" : ""} wartet auf Freigabe</span>
        <span className="text-xs font-normal text-amber-800">· Lohnzahlung für diese Personen ist blockiert</span>
      </div>
      <div className="mt-3 space-y-2">
        {items.map((it) => (
          <div key={it.employee_id} className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md bg-white/70 px-3 py-2 text-sm" data-testid={`iban-approval-row-${it.employee_id}`}>
            <span className="font-medium text-slate-900 min-w-[160px]">{it.employee_name}</span>
            {!compact && (
              <span className="font-mono text-xs text-slate-600">
                {fmtIban(it.old_iban)} <span className="text-slate-400">→</span> <span className="text-slate-900">{fmtIban(it.new_iban)}</span>
              </span>
            )}
            <span className="text-xs text-slate-500">
              via {it.source_label}{it.changed_by ? ` (${it.changed_by})` : ""} · {it.changed_at ? new Date(it.changed_at).toLocaleDateString("de-CH") : ""}
            </span>
            <div className="ml-auto flex gap-2">
              <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" disabled={!!busy}
                onClick={() => decide(it.employee_id, "approve")} data-testid={`iban-approve-${it.employee_id}`}>
                <Check className="h-3.5 w-3.5 mr-1" /> Freigeben
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-xs border-rose-300 text-rose-700 hover:bg-rose-50" disabled={!!busy}
                onClick={() => decide(it.employee_id, "reject")} data-testid={`iban-reject-${it.employee_id}`}>
                <X className="h-3.5 w-3.5 mr-1" /> Ablehnen
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
